import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { SKOOL_JOIN_URL } from "@/components/MembersOnlyBox";
import { ENTRY_PLAN } from "@/data/site";
import { useAuth } from "@/hooks/useAuth";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { canUseApps } from "@/lib/appsAccess";
import { Crown, Cpu, Laptop, MessageSquareText, Palette, Lock, Check, ArrowRight } from "lucide-react";

/**
 * /apps — Members Apps. Public landing (so it can sell the upgrade); the apps themselves
 * open only for paid site plans and Skool Premium / VIP. See lib/appsAccess.ts.
 */

type App = { slug: string; name: string; blurb: string; icon: typeof Cpu; live: boolean };

const APPS: App[] = [
  {
    slug: "prompt-maker",
    name: "Prompt Maker",
    blurb:
      "Describe what you want in plain words. It asks one question at a time, picks the right framework, and hands you a zip with the prompt, how to run it, and the whole conversation. Claude, ChatGPT, Gemini, DeepSeek, coding agents and local models.",
    icon: MessageSquareText,
    live: true,
  },
  {
    slug: "brand-pack",
    name: "Brand Pack Studio",
    blurb:
      "Answer a few questions about your business. Download a complete brand kit: logo in every version, colors with web tokens, fonts, voice and taglines, social profile and cover images, business card, and an editable brand book with steps to make your AI avatar in HeyGen.",
    icon: Palette,
    live: true,
  },
];

const REASONS = [
  {
    icon: Laptop,
    title: "Nothing to install",
    body: "No Docker, no Node, no API keys on your machine. Open the page and use the app.",
  },
  {
    icon: Cpu,
    title: "Your machine stays light",
    body: "Old laptop, work computer you can't install on, or a box already at its limit. The heavy part runs on our server.",
  },
  {
    icon: Crown,
    title: "The same apps we build in class",
    body: "Every app here is one we taught in the community. Build it yourself in the lesson, or just use it here.",
  },
];

const Apps = () => {
  const { user } = useAuth();
  const { hasAccess, planName, loading } = usePaymentStatus();
  const { toast } = useToast();
  const [buying, setBuying] = useState(false);
  const allowed = canUseApps(hasAccess, planName);

  const buyMonth = async () => {
    setBuying(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", { body: { plan: "monthly" } });
      if (error) throw error;
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.href = data.url;
    } catch (err: any) {
      toast({ title: "Payment Error", description: err?.message || "Could not start checkout.", variant: "destructive" });
      setBuying(false);
    }
  };

  return (
    <main className="min-h-screen bg-background text-foreground">
      <Seo
        title="Members Apps"
        description="Apps from the AI Automation Base classes, running on our server. For paid members and Skool Premium and VIP members. Nothing to install."
        path="/apps"
      />

      <section className="bg-gradient-hero px-6 py-20 md:py-28">
        <div className="container mx-auto max-w-3xl">
          <p className="text-sm font-medium text-primary mb-4 flex items-center gap-2">
            <Crown className="w-4 h-4" /> Members Apps
          </p>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            The apps from class, already running.
          </h1>
          <p className="mt-6 text-lg md:text-xl leading-8 text-muted-foreground">
            This section is for Premium and VIP members of the Skool community and for paid members of
            aiautobase.com. Every app and resource we teach in the community lives here, ready to use, for the
            days you don't want to install it on your own system, or your system can't take one more thing.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Building it yourself is still the lesson. This is the shortcut when you need the tool today.
          </p>
        </div>
      </section>

      <section className="px-6 py-14">
        <div className="container mx-auto max-w-5xl grid gap-6 md:grid-cols-3">
          {REASONS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-lg border border-border p-6">
              <Icon className="w-6 h-6 text-primary mb-3" />
              <h2 className="font-semibold mb-2">{title}</h2>
              <p className="text-sm leading-6 text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 pb-14">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-2xl font-bold mb-6">Apps</h2>
          <div className="grid gap-6 md:grid-cols-2">
            {APPS.map(({ slug, name, blurb, icon: Icon, live }) => (
              <div key={slug} className="rounded-lg border border-border p-6 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <Icon className="w-6 h-6 text-primary" />
                  <span className="text-xs rounded-full border border-border px-2 py-0.5 text-muted-foreground">
                    {live ? (allowed ? "Open" : "Members") : "Coming soon"}
                  </span>
                </div>
                <h3 className="text-lg font-semibold mb-2">{name}</h3>
                <p className="text-sm leading-6 text-muted-foreground flex-1">{blurb}</p>
                {live && allowed ? (
                  <Button asChild className="mt-4">
                    <Link to={`/apps/${slug}`}>
                      Open {name} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="access" className="px-6 pb-24">
        <div className="container mx-auto max-w-3xl rounded-lg border border-primary/40 p-8">
          {loading ? (
            <p className="text-muted-foreground">Checking your access…</p>
          ) : allowed ? (
            <>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Check className="w-6 h-6 text-primary" /> You're in
              </h2>
              <p className="mt-3 text-muted-foreground">
                Your plan ({planName || "member"}) includes Members Apps. New apps show up here as we build them in class.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Lock className="w-6 h-6 text-primary" /> Two ways in
              </h2>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                <div>
                  <h3 className="font-semibold">Already in the Skool community?</h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    Move up to Premium or VIP on Skool. Then sign in here with the same email; access syncs within a day.
                  </p>
                  <Button asChild variant="outline" className="mt-4 w-full">
                    <a href={SKOOL_JOIN_URL} target="_blank" rel="noopener noreferrer">
                      Upgrade on Skool
                    </a>
                  </Button>
                </div>
                <div>
                  <h3 className="font-semibold">Not on Skool?</h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    Take one month of aiautobase.com for ${ENTRY_PLAN.amount}. Apps, Build Packs, Prompt Library and the
                    Classroom, all included. Yearly and lifetime are on the pricing table.
                  </p>
                  {user ? (
                    <Button className="mt-4 w-full" onClick={buyMonth} disabled={buying}>
                      {buying ? "Opening checkout…" : `Get one month, $${ENTRY_PLAN.amount}`}
                    </Button>
                  ) : (
                    <Button asChild className="mt-4 w-full">
                      <Link to="/auth">Sign in to get one month</Link>
                    </Button>
                  )}
                  <a href="/#pricing" className="mt-2 block text-center text-sm text-muted-foreground hover:text-foreground">
                    See all plans
                  </a>
                </div>
              </div>
              {!user ? (
                <p className="mt-6 text-xs text-muted-foreground">
                  Already a member (Lifetime, Premium or VIP)? <Link to="/auth" className="underline">Sign in</Link> and this page opens up.
                </p>
              ) : null}
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default Apps;
